import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../../mocks/products';

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://songlimkorea.com';

    document.title = '자동차 교환부품 목록 | 라이트·범퍼·머드가드 - 송림코리아';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '송림코리아 자동차 교환부품 전체 목록. 라이트, 범퍼, 머드가드, 글로우 플러그, 램프류 등 다양한 차량 교환부품을 확인하세요.');

    const existingScripts = document.querySelectorAll('script[data-schema="shop"]');
    existingScripts.forEach(s => s.remove());

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "자동차 교환부품 목록 - 송림코리아",
      "description": "라이트, 범퍼, 머드가드, 글로우 플러그 등 고품질 자동차 교환부품 전체 목록",
      "url": `${siteUrl}/shop`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "홈", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "제품 목록", "item": `${siteUrl}/shop` }
        ]
      }
    };

    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "송림코리아 자동차 교환부품",
      "description": "라이트, 범퍼, 머드가드 등 차량 교환부품 목록",
      "url": `${siteUrl}/shop`,
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 10).map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": p.description,
        "url": `${siteUrl}/product/${p.name}`
      }))
    };

    [webPageSchema, itemListSchema].forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema', 'shop');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema="shop"]').forEach(s => s.remove());
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = ['전체', ...Array.from(new Set(products.map(p => p.category)))];

  // 카테고리별 카운트: 검색어 기준
  const getCategoryCount = (category: string) => {
    return products.filter(p => {
      const matchSearch = !searchTerm.trim() || 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.partNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(p.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.compatibleVehicles && p.compatibleVehicles.some(v => v.toLowerCase().includes(searchTerm.toLowerCase())));
      const matchCategory = category === '전체' || p.category === category;
      return matchCategory && matchSearch;
    }).length;
  };

  const filteredProducts = products.filter(p => {
    const matchCategory = selectedCategory === '전체' || p.category === selectedCategory;
    const matchSearch = !searchTerm.trim() || 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.partNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(p.id).toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.compatibleVehicles && p.compatibleVehicles.some(v => v.toLowerCase().includes(searchTerm.toLowerCase())));
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      {/* Hero Section */}
      <section className="relative h-[50vh] md:h-[60vh] lg:h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/b2236c4bbf96117a5cefb593a459f415.jpg"
            alt="Shop"
            className="w-full h-[120%] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 text-center px-4 md:px-8 max-w-[1400px] mx-auto w-full">
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-4 md:mb-6 leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            자동차 부품
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            고품질 글로우 플러그, 램프류, 소음기 밴드 전문 판매
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-6 md:py-10 lg:py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-6 md:mb-8">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-[#6B5744] text-lg"></i>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="부품명, 부품코드, 품번, 차종으로 검색..."
                className="w-full pl-12 pr-4 py-3 md:py-3.5 bg-[#F5F1E8] border-2 border-[#3D2817]/20 rounded-lg text-sm md:text-base text-[#3D2817] placeholder-[#6B5744]/50 focus:outline-none focus:border-[#3D2817] transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer text-[#6B5744] hover:text-[#3D2817]"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>
          </div>

          {/* Category Filters */}
          <div className="mb-4">
            <h3 className="text-sm md:text-base font-bold text-[#3D2817] mb-3 text-center" style={{ fontFamily: "'Inter', sans-serif" }}>
              부품 종류
            </h3>
            <div className="flex flex-wrap justify-center gap-2 md:gap-3">
              {categories.map((category) => {
                const count = getCategoryCount(category);
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 md:px-6 lg:px-8 py-2 md:py-2.5 text-xs md:text-sm font-bold tracking-wider transition-all duration-300 whitespace-nowrap cursor-pointer rounded-md ${
                      selectedCategory === category
                        ? 'bg-[#E63946] text-white'
                        : count === 0
                          ? 'bg-transparent text-[#E63946]/30 border-2 border-[#E63946]/20 cursor-not-allowed'
                          : 'bg-transparent text-[#E63946] border-2 border-[#E63946] hover:bg-[#E63946] hover:text-white'
                    }`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    disabled={count === 0 && category !== '전체'}
                  >
                    {category} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Results Count */}
          <div className="text-center mt-4 md:mt-6">
            <span className="text-sm text-[#6B5744]" style={{ fontFamily: "'Inter', sans-serif" }}>
              {filteredProducts.length}개 제품
            </span>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-10 md:py-20 lg:py-28 bg-[#F5F1E8]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
            {filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group cursor-pointer"
                data-product-shop
                onMouseEnter={() => setHoveredId(product.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Image Container */}
                <div className="relative w-full h-[180px] sm:h-[220px] md:h-[300px] lg:h-[380px] mb-3 md:mb-5 overflow-hidden bg-white shadow-md rounded-lg">
                  <img
                    src={product.image}
                    alt={product.description}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  
                  {/* Status Badge */}
                  {product.status === '예약주문' && (
                    <div className="absolute top-2 left-2 md:top-4 md:left-4 px-2 md:px-4 py-1 md:py-2 bg-[#B8860B] rounded-md">
                      <span className="text-white text-[10px] md:text-sm font-bold tracking-wider" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                        예약주문
                      </span>
                    </div>
                  )}

                  {/* Hover Overlay - desktop only */}
                  <div 
                    className={`absolute inset-0 bg-black/60 hidden md:flex flex-col items-center justify-center transition-opacity duration-500 ${
                      hoveredId === product.id ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <span className="text-white text-lg font-bold tracking-wider" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      상세보기
                    </span>
                  </div>
                </div>

                {/* Product Info */}
                <div className="text-center">
                  <div className="inline-block px-2 py-0.5 md:px-3 md:py-1 bg-[#E63946]/10 rounded-full mb-1.5 md:mb-3">
                    <span className="text-[10px] md:text-sm text-[#E63946]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {product.category}
                    </span>
                  </div>
                  
                  {/* 부품 코드 */}
                  <p className="text-[9px] md:text-xs text-[#3D2817] font-bold mb-1 md:mb-1.5 tracking-wider" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {product.name}
                  </p>
                  
                  {/* 부품 이름 */}
                  <h3 className="text-xs sm:text-sm md:text-base lg:text-lg font-bold text-[#3D2817] mb-1 md:mb-2 tracking-wider line-clamp-2 leading-snug" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                    {product.description}
                  </h3>

                  {/* 규격 번호 */}
                  <p className="text-[9px] md:text-xs text-[#6B5744]/70 mb-1.5 md:mb-2 leading-relaxed line-clamp-1 md:line-clamp-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {product.partNumber}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2">
                    <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-[#E63946]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      가격 문의
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
